import React, { createContext, useContext, useState, useCallback } from "react";
import { toast } from "sonner";

export interface ComparableHome {
  nh_slug: string;
  center_name: string | null;
  county: string | null;
  county_slug: string | null;
  town: string | null;
  inspection_rating: string | null;
  fair_deal_approved: boolean | null;
  price_range: string | null;
  max_residents: number | null;
  max_occupancy: number | null;
  category: string | null;
  latitude: number | null;
  longitude: number | null;
  specializations: string[] | null;
  wheelchair_accessible: boolean | null;
  last_inspection_date: string | null;
}

interface ComparisonContextValue {
  comparedHomes: ComparableHome[];
  addToComparison: (home: ComparableHome) => void;
  removeFromComparison: (slug: string) => void;
  isCompared: (slug: string) => boolean;
  clearComparison: () => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const ComparisonContext = createContext<ComparisonContextValue | null>(null);

export function ComparisonProvider({ children }: { children: React.ReactNode }) {
  const [comparedHomes, setComparedHomes] = useState<ComparableHome[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const addToComparison = useCallback((home: ComparableHome) => {
    setComparedHomes((prev) => {
      if (prev.find((h) => h.nh_slug === home.nh_slug)) return prev;
      if (prev.length >= 4) {
        toast.error("You can compare up to 4 homes at once.");
        return prev;
      }
      toast.success(`${home.center_name} added to comparison`);
      return [...prev, home];
    });
  }, []);

  const removeFromComparison = useCallback((slug: string) => {
    setComparedHomes((prev) => prev.filter((h) => h.nh_slug !== slug));
  }, []);

  const isCompared = useCallback(
    (slug: string) => comparedHomes.some((h) => h.nh_slug === slug),
    [comparedHomes]
  );

  const clearComparison = useCallback(() => {
    setComparedHomes([]);
    setIsOpen(false);
  }, []);

  return (
    <ComparisonContext.Provider
      value={{ comparedHomes, addToComparison, removeFromComparison, isCompared, clearComparison, isOpen, setIsOpen }}
    >
      {children}
    </ComparisonContext.Provider>
  );
}

export function useComparison() {
  const ctx = useContext(ComparisonContext);
  if (!ctx) throw new Error("useComparison must be used inside ComparisonProvider");
  return ctx;
}
